import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import ClaimLocationModal from '../components/modals/ClaimLocationModal';
import DisputeLocationModal from '../components/modals/DisputeLocationModal';

// Icons
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const StarIcon = ({ filled = false }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const HeartIcon = ({ filled }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const TIER_CONFIG = {
  0: { name: 'Listed', color: '#6b7280', badge: null },
  1: { name: 'Basic Partner', color: '#0ea5e9', badge: 'Basic' },
  2: { name: 'Verified Partner', color: '#22c55e', badge: 'Verified' },
  3: { name: 'Premium Partner', color: '#f59e0b', badge: 'Premium' },
  4: { name: 'Premium Partner', color: '#f59e0b', badge: 'Premium' },
};

// Weekly event limits by tier
const TIER_EVENT_LIMITS = {
  0: 0,  // Free
  1: 2,  // Basic
  2: 2,  // Enhanced
  3: 4,  // Premium
};

const DAYS_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const ShieldCheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ClockSmallIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const LocationDetail = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { locationId } = useParams();
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(null);
  const [followerCount, setFollowerCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [weeklyEvents, setWeeklyEvents] = useState([]);

  // Claim state
  const [claimStatus, setClaimStatus] = useState(null); // unclaimed, claimed_by_me_pending, claimed_by_other, is_owner
  const [currentOwnerProfileId, setCurrentOwnerProfileId] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showClaimConfirm, setShowClaimConfirm] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);

  const currentUserId = activeProfile?.id;

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        setLoading(true);

        // Fetch location
        const { data: locData, error } = await supabase
          .from('locations')
          .select('*')
          .eq('id', locationId)
          .single();

        if (error) throw error;
        setLocation(locData);

        // Fetch ratings (using new schema with cleanliness, friendliness, space)
        const { data: ratingsData } = await supabase
          .from('location_ratings')
          .select('cleanliness_rating, friendliness_rating, space_rating, staff_helped, staff_rating')
          .eq('location_id', locationId);

        if (ratingsData && ratingsData.length > 0) {
          // Calculate averages for each category
          let totalCleanliness = 0, countCleanliness = 0;
          let totalFriendliness = 0, countFriendliness = 0;
          let totalSpace = 0, countSpace = 0;
          let totalStaff = 0, countStaff = 0;

          ratingsData.forEach(r => {
            if (r.cleanliness_rating) { totalCleanliness += r.cleanliness_rating; countCleanliness++; }
            if (r.friendliness_rating) { totalFriendliness += r.friendliness_rating; countFriendliness++; }
            if (r.space_rating) { totalSpace += r.space_rating; countSpace++; }
            if (r.staff_helped && r.staff_rating) { totalStaff += r.staff_rating; countStaff++; }
          });

          const avgCleanliness = countCleanliness > 0 ? totalCleanliness / countCleanliness : 0;
          const avgFriendliness = countFriendliness > 0 ? totalFriendliness / countFriendliness : 0;
          const avgSpace = countSpace > 0 ? totalSpace / countSpace : 0;
          const avgStaff = countStaff > 0 ? totalStaff / countStaff : 0;

          // Overall average
          const overallAvg = (avgCleanliness + avgFriendliness + avgSpace) / 3;

          setRating({
            avg: overallAvg,
            count: ratingsData.length,
            breakdown: {
              cleanliness: avgCleanliness,
              friendliness: avgFriendliness,
              space: avgSpace,
              staff: avgStaff,
              staffCount: countStaff,
            },
          });
        }

        // Fetch follower count
        const { count } = await supabase
          .from('location_followers')
          .select('*', { count: 'exact', head: true })
          .eq('location_id', locationId);

        setFollowerCount(count || 0);

        // Check if current user follows
        if (currentUserId) {
          const { data: followData } = await supabase
            .from('location_followers')
            .select('id')
            .eq('location_id', locationId)
            .eq('follower_id', currentUserId)
            .single();

          setIsFollowing(!!followData);
        }

        // Fetch events (only for Basic+ tiers)
        if ((locData.subscription_tier || 0) >= 1) {
          const { data: eventsData } = await supabase
            .from('location_events')
            .select('*')
            .eq('location_id', locationId)
            .eq('is_recurring', false)
            .gte('event_date', new Date().toISOString().split('T')[0])
            .order('event_date', { ascending: true })
            .limit(5);

          setEvents(eventsData || []);

          // Fetch weekly recurring events
          const tierLimit = TIER_EVENT_LIMITS[locData.subscription_tier || 0] || 0;
          if (tierLimit > 0) {
            const { data: weeklyData } = await supabase
              .from('location_events')
              .select('*')
              .eq('location_id', locationId)
              .eq('is_recurring', true)
              .order('recurrence_day', { ascending: true });

            // Only show events up to tier limit
            setWeeklyEvents((weeklyData || []).slice(0, tierLimit));
          }
        }
      } catch (err) {
        console.error('Error fetching location:', err);
      } finally {
        setLoading(false);
      }
    };

    if (locationId) {
      fetchLocation();
    }
  }, [locationId, currentUserId]);

  // Check claim status
  useEffect(() => {
    const checkClaimStatus = async () => {
      if (!location || !currentUserId || !user) return;

      // Check if current user is the owner
      if (location.owner_id === user.id) {
        setClaimStatus('is_owner');
        return;
      }

      // Check if location has an owner (someone else)
      if (location.owner_id) {
        // Look up owner's profile ID for dispute flow
        const { data: ownerProfile } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', location.owner_id)
          .single();
        if (ownerProfile) setCurrentOwnerProfileId(ownerProfile.id);
        setClaimStatus('claimed_by_other');
        return;
      }

      // Check if current user has a pending claim
      const { data: pendingClaim } = await supabase
        .from('location_claims')
        .select('id, status')
        .eq('location_id', locationId)
        .eq('claimant_id', currentUserId)
        .eq('status', 'pending')
        .single();

      if (pendingClaim) {
        setClaimStatus('claimed_by_me_pending');
        return;
      }

      setClaimStatus('unclaimed');
    };

    checkClaimStatus();
  }, [location, currentUserId, user, locationId]);

  const handleToggleFollow = async () => {
    if (!currentUserId || followLoading) return;

    setFollowLoading(true);
    const wasFollowing = isFollowing;

    // Optimistic update
    setIsFollowing(!wasFollowing);
    setFollowerCount(prev => wasFollowing ? prev - 1 : prev + 1);

    try {
      if (wasFollowing) {
        await supabase
          .from('location_followers')
          .delete()
          .eq('follower_id', currentUserId)
          .eq('location_id', locationId);
      } else {
        await supabase
          .from('location_followers')
          .insert({ follower_id: currentUserId, location_id: locationId });
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
      // Revert
      setIsFollowing(wasFollowing);
      setFollowerCount(prev => wasFollowing ? prev + 1 : prev - 1);
    } finally {
      setFollowLoading(false);
    }
  };

  // Parse operating hours
  const parseHours = () => {
    if (!location?.operating_hours) return null;

    try {
      const hours = typeof location.operating_hours === 'string'
        ? JSON.parse(location.operating_hours)
        : location.operating_hours;

      return DAYS_ORDER.map(day => {
        const value = hours[day] || hours[day.toLowerCase()] || 'Closed';
        return { day, hours: value };
      });
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme.bg }}>
        <div style={styles.loadingContainer}>
          <div style={{ ...styles.spinner, borderTopColor: theme.accent }} />
        </div>
      </div>
    );
  }

  if (!location) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme.bg }}>
        <div style={styles.header}>
          <button style={{ ...styles.backButton, color: theme.text }} onClick={() => navigate(-1)}>
            <ArrowLeftIcon />
          </button>
        </div>
        <div style={styles.errorContainer}>
          <span style={{ color: theme.textSecondary }}>Location not found</span>
        </div>
      </div>
    );
  }

  const tier = location.subscription_tier || 0;
  const tierConfig = TIER_CONFIG[tier];
  const parsedHours = parseHours();

  // Parse photos array
  const photos = (() => {
    try {
      if (!location.photos) return [];
      if (typeof location.photos === 'string') return JSON.parse(location.photos);
      return location.photos;
    } catch {
      return [];
    }
  })();

  return (
    <div style={{ ...styles.container, backgroundColor: theme.bg }}>
      {/* Header - overlaid on hero if photo exists */}
      <div style={{
        ...styles.header,
        borderBottomColor: location.photo_url ? 'transparent' : theme.border,
        position: location.photo_url ? 'absolute' : 'relative',
        zIndex: 10,
        backgroundColor: location.photo_url ? 'transparent' : theme.bg,
      }}>
        <button style={{
          ...styles.backButton,
          color: location.photo_url ? '#fff' : theme.text,
          backgroundColor: location.photo_url ? 'rgba(0,0,0,0.4)' : 'transparent',
          borderRadius: 20,
        }} onClick={() => navigate(-1)}>
          <ArrowLeftIcon />
        </button>
        <div style={styles.headerSpacer} />
        <div style={styles.headerSpacer} />
      </div>

      {/* Content */}
      <div style={{ ...styles.content, paddingTop: location.photo_url ? 0 : 16 }}>
        {/* Hero Image */}
        {location.photo_url && (
          <div style={styles.heroImageContainer}>
            <img
              src={location.photo_url}
              alt={location.store_name}
              style={styles.heroImage}
            />
            <div style={styles.heroGradient} />
          </div>
        )}

        {/* Info Section */}
        <div style={{
          ...styles.heroSection,
          backgroundColor: theme.bgSheet,
          border: `2px solid ${tierConfig.color}`,
          marginTop: location.photo_url ? -40 : 0,
          position: 'relative',
          zIndex: 5,
        }}>
          {/* Tier Badge */}
          {tier >= 1 && (
            <div style={{
              ...styles.tierBadge,
              backgroundColor: `${tierConfig.color}20`,
              borderColor: tierConfig.color,
              color: tierConfig.color,
            }}>
              <CheckCircleIcon />
              <span>{tierConfig.badge} Partner</span>
            </div>
          )}

          <h2 style={{ ...styles.storeName, color: theme.text }}>{location.store_name}</h2>

          {/* Address */}
          <div style={styles.addressRow}>
            <MapPinIcon />
            <div style={{ marginLeft: 8 }}>
              <div style={{ color: theme.text }}>{location.address}</div>
              <div style={{ color: theme.textSecondary, fontSize: 14 }}>
                {location.city}, {location.state} {location.zip_code}
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div style={{ ...styles.statsRow, borderTopColor: theme.border }}>
            <div style={styles.statItem}>
              <span style={{ color: '#fbbf24' }}><StarIcon filled /></span>
              <span style={{ ...styles.statValue, color: theme.text }}>
                {rating ? rating.avg.toFixed(1) : 'N/A'}
              </span>
              <span style={{ ...styles.statLabel, color: theme.textSecondary }}>
                ({rating?.count || 0})
              </span>
            </div>
            <div style={{ ...styles.statDivider, backgroundColor: theme.border }} />
            <div style={styles.statItem}>
              <UsersIcon />
              <span style={{ ...styles.statValue, color: theme.text }}>{followerCount}</span>
              <span style={{ ...styles.statLabel, color: theme.textSecondary }}>followers</span>
            </div>
          </div>

          {/* Follow Button */}
          {currentUserId && (
            <button
              style={{
                ...styles.followButton,
                backgroundColor: isFollowing ? theme.accent : 'transparent',
                borderColor: theme.accent,
                color: isFollowing ? '#fff' : theme.accent,
                opacity: followLoading ? 0.6 : 1,
              }}
              onClick={handleToggleFollow}
              disabled={followLoading}
            >
              <HeartIcon filled={isFollowing} />
              <span>{isFollowing ? 'Following' : 'Follow'}</span>
            </button>
          )}

          {/* Claim / Dispute Button */}
          {currentUserId && claimStatus === 'unclaimed' && (
            <button
              style={{
                background: 'none',
                border: 'none',
                color: theme.textMuted,
                fontSize: 12,
                cursor: 'pointer',
                marginTop: 8,
                padding: '4px 0',
                textDecoration: 'underline',
              }}
              onClick={() => setShowClaimConfirm(true)}
            >
              Own this business?
            </button>
          )}

          {currentUserId && claimStatus === 'claimed_by_me_pending' && (
            <button
              style={{
                ...styles.followButton,
                backgroundColor: 'rgba(245, 158, 11, 0.15)',
                borderColor: '#f59e0b',
                color: '#f59e0b',
                marginTop: 8,
                gap: 8,
                cursor: 'default',
              }}
              disabled
            >
              <ClockSmallIcon />
              <span>Claim Pending Review</span>
            </button>
          )}

          {currentUserId && claimStatus === 'claimed_by_other' && (
            <button
              style={{
                ...styles.followButton,
                backgroundColor: 'transparent',
                borderColor: '#ef4444',
                color: '#ef4444',
                marginTop: 8,
                gap: 8,
              }}
              onClick={() => setShowDisputeModal(true)}
            >
              <AlertTriangleIcon />
              <span>Dispute Ownership</span>
            </button>
          )}
        </div>

        {/* Ratings Breakdown */}
        {rating?.breakdown && (
          <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
            <h3 style={{ ...styles.sectionTitle, color: theme.text }}>
              <StarIcon filled />
              <span style={{ marginLeft: 8 }}>Ratings ({rating.count} reviews)</span>
            </h3>
            <div style={styles.ratingsBreakdown}>
              <div style={styles.ratingBarRow}>
                <span style={{ ...styles.ratingBarLabel, color: theme.text }}>Cleanliness</span>
                <div style={{ ...styles.ratingBarTrack, backgroundColor: theme.border }}>
                  <div style={{ ...styles.ratingBarFill, width: `${(rating.breakdown.cleanliness / 5) * 100}%`, backgroundColor: theme.accent }} />
                </div>
                <span style={{ ...styles.ratingBarValue, color: theme.text }}>{rating.breakdown.cleanliness.toFixed(1)}</span>
              </div>
              <div style={styles.ratingBarRow}>
                <span style={{ ...styles.ratingBarLabel, color: theme.text }}>Friendliness</span>
                <div style={{ ...styles.ratingBarTrack, backgroundColor: theme.border }}>
                  <div style={{ ...styles.ratingBarFill, width: `${(rating.breakdown.friendliness / 5) * 100}%`, backgroundColor: theme.accent }} />
                </div>
                <span style={{ ...styles.ratingBarValue, color: theme.text }}>{rating.breakdown.friendliness.toFixed(1)}</span>
              </div>
              <div style={styles.ratingBarRow}>
                <span style={{ ...styles.ratingBarLabel, color: theme.text }}>Space</span>
                <div style={{ ...styles.ratingBarTrack, backgroundColor: theme.border }}>
                  <div style={{ ...styles.ratingBarFill, width: `${(rating.breakdown.space / 5) * 100}%`, backgroundColor: theme.accent }} />
                </div>
                <span style={{ ...styles.ratingBarValue, color: theme.text }}>{rating.breakdown.space.toFixed(1)}</span>
              </div>
              {rating.breakdown.staffCount > 0 && (
                <div style={styles.ratingBarRow}>
                  <span style={{ ...styles.ratingBarLabel, color: theme.text }}>Staff</span>
                  <div style={{ ...styles.ratingBarTrack, backgroundColor: theme.border }}>
                    <div style={{ ...styles.ratingBarFill, width: `${(rating.breakdown.staff / 5) * 100}%`, backgroundColor: theme.accent }} />
                  </div>
                  <span style={{ ...styles.ratingBarValue, color: theme.text }}>{rating.breakdown.staff.toFixed(1)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Photo Gallery - Only for tiers with multiple photos */}
        {photos.length > 1 && (
          <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
            <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Photos ({photos.length})</h3>
            <div style={styles.photoGrid}>
              {photos.map((photo, index) => (
                <div key={index} style={styles.photoThumb}>
                  <img src={photo} alt={`${location.store_name} ${index + 1}`} style={styles.photoThumbImg} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        {location.description && (
          <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
            <h3 style={{ ...styles.sectionTitle, color: theme.text }}>About</h3>
            <p style={{ ...styles.description, color: theme.textSecondary }}>
              {location.description}
            </p>
          </div>
        )}

        {/* Contact Info */}
        {(location.phone || location.website || location.email) && (
          <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
            <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Contact</h3>

            {location.phone && (
              <a href={`tel:${location.phone}`} style={{ ...styles.contactRow, color: theme.text }}>
                <PhoneIcon />
                <span style={styles.contactText}>{location.phone}</span>
              </a>
            )}

            {location.website && (
              <a
                href={location.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...styles.contactRow, color: theme.accent }}
              >
                <GlobeIcon />
                <span style={styles.contactText}>{location.website.replace(/^https?:\/\//, '')}</span>
              </a>
            )}
          </div>
        )}

        {/* Operating Hours */}
        {parsedHours && (
          <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
            <h3 style={{ ...styles.sectionTitle, color: theme.text }}>
              <ClockIcon />
              <span style={{ marginLeft: 8 }}>Hours</span>
            </h3>

            <div style={styles.hoursGrid}>
              {parsedHours.map(({ day, hours }) => (
                <div key={day} style={styles.hoursRow}>
                  <span style={{ ...styles.dayLabel, color: theme.textSecondary }}>{day}</span>
                  <span style={{
                    ...styles.hoursValue,
                    color: hours === 'Closed' ? theme.error : theme.text,
                  }}>
                    {hours}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Weekly Schedule Section - Basic+ tiers only */}
        {tier >= 1 && weeklyEvents.length > 0 && (
          <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
            <h3 style={{ ...styles.sectionTitle, color: theme.text }}>
              <CalendarIcon />
              <span style={{ marginLeft: 8 }}>Weekly Schedule</span>
            </h3>
            <div style={styles.weeklyList}>
              {weeklyEvents.map(event => {
                // Handle recurrence_day as index (0-6) or string day name
                let dayName = null;
                if (typeof event.recurrence_day === 'number') {
                  dayName = DAYS_ORDER[event.recurrence_day];
                } else if (typeof event.recurrence_day === 'string') {
                  const asNum = parseInt(event.recurrence_day);
                  if (!isNaN(asNum) && asNum >= 0 && asNum <= 6) {
                    dayName = DAYS_ORDER[asNum];
                  } else {
                    dayName = event.recurrence_day;
                  }
                }
                // Fallback: extract day from event name (e.g., "Friday Night Pokemon")
                if (!dayName && event.name) {
                  const dayMatch = event.name.match(/^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)/i);
                  if (dayMatch) {
                    dayName = dayMatch[1].charAt(0).toUpperCase() + dayMatch[1].slice(1).toLowerCase();
                  }
                }
                dayName = dayName || 'Weekly';
                const formatTime = (t) => {
                  if (!t) return '';
                  const [h, m] = t.split(':');
                  const hour = parseInt(h);
                  const ampm = hour >= 12 ? 'PM' : 'AM';
                  const hour12 = hour % 12 || 12;
                  return `${hour12}:${m} ${ampm}`;
                };
                const startTime = formatTime(event.start_time);
                const endTime = formatTime(event.end_time);

                return (
                  <div key={event.id} style={{ ...styles.weeklyCard, borderColor: theme.border }}>
                    <div style={{ ...styles.weeklyDay, color: theme.accent }}>
                      {dayName}s
                    </div>
                    <div style={styles.weeklyInfo}>
                      <span style={{ ...styles.weeklyName, color: theme.text }}>{event.name}</span>
                      <span style={{ ...styles.weeklyTime, color: theme.textSecondary }}>
                        {startTime}{endTime ? ` - ${endTime}` : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Events Section - Basic+ tiers only */}
        {tier >= 1 && (
          <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
            <h3 style={{ ...styles.sectionTitle, color: theme.text }}>
              <CalendarIcon />
              <span style={{ marginLeft: 8 }}>Upcoming Events ({events.length})</span>
            </h3>
            {events.length > 0 ? (
              <div style={styles.eventsList}>
                {events.map(event => {
                  const eventDate = new Date(event.event_date);
                  const dayName = eventDate.toLocaleDateString('en-US', { weekday: 'short' });
                  const monthDay = eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const startTime = event.start_time ? event.start_time.slice(0, 5) : null;
                  const endTime = event.end_time ? event.end_time.slice(0, 5) : null;
                  const formatTime = (t) => {
                    if (!t) return '';
                    const [h, m] = t.split(':');
                    const hour = parseInt(h);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    return `${hour12}:${m} ${ampm}`;
                  };

                  return (
                    <div key={event.id} style={{ ...styles.eventCard, borderColor: theme.border }}>
                      <div style={styles.eventDateBox}>
                        <span style={{ ...styles.eventDay, color: theme.accent }}>{dayName}</span>
                        <span style={{ ...styles.eventDate, color: theme.text }}>{monthDay}</span>
                      </div>
                      <div style={styles.eventInfo}>
                        <span style={{ ...styles.eventName, color: theme.text }}>{event.name}</span>
                        {startTime && (
                          <span style={{ ...styles.eventTime, color: theme.textSecondary }}>
                            {formatTime(startTime)}{endTime ? ` - ${formatTime(endTime)}` : ''}
                          </span>
                        )}
                        {event.description && (
                          <span style={{ ...styles.eventDesc, color: theme.textMuted }}>
                            {event.description.length > 60 ? event.description.slice(0, 60) + '...' : event.description}
                          </span>
                        )}
                        <div style={styles.eventMeta}>
                          {event.rsvp_count > 0 && (
                            <span style={{ ...styles.eventRsvp, color: theme.accent }}>
                              {event.rsvp_count} going
                            </span>
                          )}
                          {event.is_recurring && (
                            <span style={{ ...styles.eventRecurring, backgroundColor: `${theme.accent}20`, color: theme.accent }}>
                              Weekly
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ ...styles.noEvents, color: theme.textMuted }}>
                No upcoming events scheduled
              </p>
            )}
          </div>
        )}
      </div>

      {/* Claim Modal */}
      {/* Claim Confirmation Popup */}
      {showClaimConfirm && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }} onClick={() => setShowClaimConfirm(false)}>
          <div style={{
            backgroundColor: theme.bgCard, borderRadius: 16, padding: 24,
            maxWidth: 340, width: '100%', textAlign: 'center',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: theme.text, marginBottom: 8 }}>
              Claim this location?
            </div>
            <p style={{ fontSize: 13, color: theme.textSecondary, lineHeight: 1.5, margin: '0 0 20px' }}>
              This is only for verified business owners. False claims may result in account action.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  border: `1px solid ${theme.border}`, backgroundColor: 'transparent',
                  color: theme.text, fontSize: 14, fontWeight: 500, cursor: 'pointer',
                }}
                onClick={() => setShowClaimConfirm(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  border: 'none', backgroundColor: theme.accent,
                  color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
                onClick={() => {
                  setShowClaimConfirm(false);
                  navigate('/my-store');
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <ClaimLocationModal
        isOpen={showClaimModal}
        onClose={() => setShowClaimModal(false)}
        locationId={locationId}
        locationData={location}
        onSuccess={() => setClaimStatus('claimed_by_me_pending')}
      />

      {/* Dispute Modal */}
      <DisputeLocationModal
        isOpen={showDisputeModal}
        onClose={() => setShowDisputeModal(false)}
        locationId={locationId}
        locationData={location}
        currentOwnerId={currentOwnerProfileId}
      />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid',
  },
  backButton: {
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    marginLeft: -8,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    margin: 0,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40,
  },
  content: {
    flex: 1,
    padding: 16,
    paddingBottom: 100,
    overflowY: 'auto',
  },
  heroImageContainer: {
    position: 'relative',
    width: 'calc(100% + 32px)',
    marginLeft: -16,
    marginTop: -16,
    height: 220,
    overflow: 'hidden',
  },
  heroImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  heroGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 80,
    background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
  },
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
  },
  photoThumb: {
    aspectRatio: '1',
    borderRadius: 8,
    overflow: 'hidden',
  },
  photoThumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid rgba(255,255,255,0.2)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroSection: {
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
  },
  tierBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 8,
    border: '1px solid',
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 12,
  },
  storeName: {
    fontSize: 24,
    fontWeight: '700',
    margin: '0 0 16px 0',
  },
  addressRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  statsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 0',
    borderTop: '1px solid',
    marginBottom: 16,
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    justifyContent: 'center',
  },
  statValue: {
    fontSize: 16,
    fontWeight: '600',
  },
  statLabel: {
    fontSize: 13,
  },
  statDivider: {
    width: 1,
    height: 24,
  },
  followButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 20px',
    borderRadius: 12,
    border: '2px solid',
    fontSize: 16,
    fontWeight: '600',
    cursor: 'pointer',
  },
  section: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    border: '1px solid',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 16,
    fontWeight: '600',
    margin: '0 0 12px 0',
  },
  description: {
    fontSize: 15,
    lineHeight: 1.6,
    margin: 0,
  },
  ratingsBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  ratingBarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  ratingBarLabel: {
    fontSize: 14,
    fontWeight: 500,
    minWidth: 100,
  },
  ratingBarTrack: {
    flex: 1,
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
  },
  ratingBarFill: {
    height: '100%',
    borderRadius: 4,
    transition: 'width 0.3s ease',
  },
  ratingBarValue: {
    fontSize: 14,
    fontWeight: 600,
    minWidth: 32,
    textAlign: 'right',
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
    textDecoration: 'none',
  },
  contactText: {
    fontSize: 15,
  },
  hoursGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  hoursRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dayLabel: {
    fontSize: 14,
  },
  hoursValue: {
    fontSize: 14,
    fontWeight: '500',
  },
  noEvents: {
    fontSize: 14,
    margin: 0,
    fontStyle: 'italic',
  },
  eventsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  eventCard: {
    display: 'flex',
    gap: 12,
    padding: 12,
    borderRadius: 10,
    border: '1px solid',
  },
  eventDateBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 50,
  },
  eventDay: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  eventDate: {
    fontSize: 14,
    fontWeight: '700',
  },
  eventInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  eventName: {
    fontSize: 15,
    fontWeight: '600',
  },
  eventTime: {
    fontSize: 13,
  },
  eventDesc: {
    fontSize: 12,
    lineHeight: 1.4,
    marginTop: 2,
  },
  eventMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 6,
  },
  eventRsvp: {
    fontSize: 12,
    fontWeight: '500',
  },
  eventRecurring: {
    fontSize: 10,
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: 4,
  },
  weeklyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  weeklyCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: 12,
    borderRadius: 10,
    border: '1px solid',
  },
  weeklyDay: {
    fontSize: 13,
    fontWeight: '700',
    minWidth: 90,
  },
  weeklyInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  weeklyName: {
    fontSize: 15,
    fontWeight: '600',
  },
  weeklyTime: {
    fontSize: 13,
  },
};

export default LocationDetail;
